import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { VolumeState } from '../VolumeControl';
import { readVolume, saveVolume } from '../VolumeControl';

const VOLUME_STEP = 0.1;
const TOAST_MS = 1500;

/** Persisted volume applied to the video element, with step/mute helpers and a toast flag. */
export function useVolume(videoRef: RefObject<HTMLVideoElement | null>) {
  const [volume, setVolumeState] = useState<VolumeState>(readVolume);
  const [toast, setToast] = useState(false);
  const current = useRef(volume);

  const setVolume = useCallback((next: VolumeState, announce = false) => {
    const clamped = { volume: Math.min(1, Math.max(0, Math.round(next.volume * 100) / 100)), muted: next.muted };
    current.current = clamped;
    setVolumeState(clamped);
    saveVolume(clamped);
    if (announce) setToast(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume.volume;
    video.muted = volume.muted;
  }, [videoRef, volume]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(false), TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast, volume]);

  const step = useCallback((direction: 1 | -1) => setVolume({ volume: current.current.volume + direction * VOLUME_STEP, muted: false }, true), [setVolume]);
  const toggleMute = useCallback(() => setVolume({ ...current.current, muted: !current.current.muted }, true), [setVolume]);

  return { volume, setVolume, step, toggleMute, toast };
}
